import { useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { SortableHeader, nextSort, type SortState } from '../ui/SortableHeader';
import { ColumnFilter } from '../ui/ColumnFilter';
import { useResizableColumns } from '../../hooks/useResizableColumns';
import { useColumnFilters } from '../../hooks/useColumnFilters';
import type { VacationFlag, VacationPlanRow, VacationPlanSummary } from '../../types/timerocket';
import { formatDateLong, formatDays, formatRange } from './format';
import { DiscrepancyBadges } from './DiscrepancyBadges';

/**
 * One row per person: today's vacation balance, what is planned to year end, the projected
 * rest and its flag. Sorted by rest DESC by default — the people with the most unused vacation
 * are the ones HR has to talk to before December (P3 #3). Table standard: sticky sortable
 * headers, drag-resizable columns, Excel-style column filters, sums over the FILTERED rows.
 *
 * The flag comes from the server (thresholds live in TimeRocketConfig); this table only colours
 * it. A "≈" marks a figure that is estimated or stale — an entry the sync could only approximate
 * (a RemainingDay entry, an unparsable time frame), a balance older than today, or a plan
 * fetched before the balance — and the cell's title says WHICH. The rest inherits the doubt, so
 * it is marked too.
 *
 * Vacation is not health data: every scope the viewer holds may see it, so there is no
 * per-row gate here and no daily-detail click — the plan has no days to open.
 */

type ColKey = 'name' | 'companyCode' | 'balanceDays' | 'plannedDays' | 'restDays' | 'nextVacation' | 'entries';

interface ColDef {
    key: ColKey;
    label: string;
    width: number;
    align?: 'right';
}

const COLUMNS: ColDef[] = [
    { key: 'name', label: 'Name', width: 200 },
    { key: 'companyCode', label: 'Company', width: 80 },
    { key: 'balanceDays', label: 'Balance', width: 100, align: 'right' },
    { key: 'plannedDays', label: 'Planned to year end', width: 140, align: 'right' },
    { key: 'restDays', label: 'Rest', width: 110, align: 'right' },
    { key: 'nextVacation', label: 'Next vacation', width: 220 },
    { key: 'entries', label: 'Entries', width: 80, align: 'right' },
];

const NUMERIC: ColKey[] = ['balanceDays', 'plannedDays', 'restDays', 'entries'];

const FILTER_LABELS = { search: 'Search…', all: 'All', none: 'None', from: 'from', to: 'to', empty: '(blank)' };

/** Zero planned is zero days whatever the rate — only a non-zero figure needs one. */
function plannedDaysOf(r: VacationPlanRow): number | null {
    return r.plannedDays ?? (r.plannedMin === 0 ? 0 : null);
}

/**
 * What a column shows to the filter popover — the SAME text the cell shows, so "Next vacation"
 * offers "14 Sep 2026 – 18 Sep 2026" to tick, not an ISO key the user never sees.
 */
const ACCESSOR: Record<ColKey, (r: VacationPlanRow) => string | number | null> = {
    name: r => r.name,
    companyCode: r => r.companyCode?.toUpperCase() ?? null,
    balanceDays: r => r.balanceDays,
    plannedDays: r => plannedDaysOf(r),
    restDays: r => r.restDays,
    nextVacation: r => (r.nextVacation ? formatRange(r.nextVacation.from, r.nextVacation.to) : null),
    entries: r => r.plannedEntries.length,
};

/**
 * Where sorting must NOT use the display text: a formatted date range sorts alphabetically
 * ("1 Oct" before "14 Sep"), so the ISO start key sorts the column chronologically instead.
 */
const SORT_ACCESSOR: Partial<Record<ColKey, (r: VacationPlanRow) => string | number | null>> = {
    nextVacation: r => (r.nextVacation ? r.nextVacation.from : null),
};

const FILTER_COLUMNS = COLUMNS.map(c => ({ key: c.key, accessor: ACCESSOR[c.key], numeric: NUMERIC.includes(c.key) }));

/** Why a row's figure is marked ≈ — the server's three reasons, spelled out for the cell title. */
function approximateTitle(r: VacationPlanRow): string {
    const reasons: string[] = [];
    if (r.plannedEntries.some(e => e.isApproximate || e.plannedMin === null)) reasons.push('estimated entry');
    if (r.staleBalance) reasons.push('balance older than today');
    if (r.stalePlan) reasons.push('plan older than balance');
    return `approximate — ${reasons.length > 0 ? reasons.join(', ') : 'estimated or stale figure'}`;
}

const FLAG_CLASS: Record<VacationFlag, string> = {
    red: 'bg-red-100 text-red-800',
    yellow: 'bg-amber-100 text-amber-800',
    none: '',
};

interface Props {
    rows: VacationPlanRow[];
    summary: VacationPlanSummary;
    loading: boolean;
    emptyMessage: string;
}

export function VacationPlanTable({ rows, summary, loading, emptyMessage }: Props) {
    const [sort, setSort] = useState<SortState<ColKey>>({ key: 'restDays', direction: 'desc' });

    const { widthOf, resizingKey, startResize, resetWidth, shouldSuppressClick } =
        useResizableColumns(COLUMNS, 'timerocket-vacation-col-widths');
    const { filtered, filters, setColumnFilter, isFiltered, activeCount, clearAll, uniqueValuesFor } =
        useColumnFilters(rows, FILTER_COLUMNS);

    const onSort = (key: ColKey) => {
        if (shouldSuppressClick()) return;
        setSort(s => nextSort(s, key, NUMERIC.includes(key)));
    };

    const sorted = useMemo(() => (sort.key
        ? [...filtered].sort((a, b) => {
            const key = sort.key as ColKey;
            const acc = SORT_ACCESSOR[key] ?? ACCESSOR[key];
            const av = acc(a);
            const bv = acc(b);
            if (av === null || av === undefined || av === '') return 1;
            if (bv === null || bv === undefined || bv === '') return -1;
            const base = typeof av === 'number' && typeof bv === 'number'
                ? av - bv
                : String(av).localeCompare(String(bv), undefined, { numeric: true });
            return base * (sort.direction === 'asc' ? 1 : -1);
        })
        : filtered), [filtered, sort]);

    // Sums over the rows that HAVE the figure — a person without a balance contributes nothing,
    // not a zero that would pull the average down.
    const sums = filtered.reduce(
        (s, r) => ({
            balance: s.balance + (r.balanceDays ?? 0),
            planned: s.planned + (plannedDaysOf(r) ?? 0),
            rest: s.rest + (r.restDays ?? 0),
            entries: s.entries + r.plannedEntries.length,
            red: s.red + (r.flag === 'red' ? 1 : 0),
            yellow: s.yellow + (r.flag === 'yellow' ? 1 : 0),
        }),
        { balance: 0, planned: 0, rest: 0, entries: 0, red: 0, yellow: 0 },
    );

    return (
        <div className="border rounded-lg bg-white overflow-hidden">
            {activeCount > 0 && (
                <div className="flex items-center justify-between px-4 py-1.5 border-b bg-amber-50 text-xs text-amber-900">
                    <span>{activeCount} filter(s) active</span>
                    <button onClick={clearAll} className="text-blue-600 hover:text-blue-800 font-medium">Clear all</button>
                </div>
            )}

            {/* The sticky header needs a bounded scroll container or it does not stick. The floor is
                for the one-row own view, so a filter popover is not clipped by this container. */}
            <div className="overflow-auto" style={{ maxHeight: '70vh', minHeight: rows.length > 0 && rows.length <= 3 ? 360 : undefined }}>
                {loading && rows.length === 0 && (
                    <div className="p-12 text-center text-gray-500">
                        <Loader2 className="h-5 w-5 animate-spin inline mr-2" />Loading…
                    </div>
                )}
                {!loading && rows.length === 0 && (
                    <div className="p-8 text-center text-gray-500">{emptyMessage}</div>
                )}
                {rows.length > 0 && filtered.length === 0 && (
                    <div className="p-8 text-center text-gray-500">{activeCount} filter(s) active — no row matches.</div>
                )}
                {rows.length > 0 && filtered.length > 0 && (
                    <table className={`w-full text-sm ${loading ? 'opacity-60' : ''}`} style={{ tableLayout: 'fixed' }}>
                        <colgroup>
                            {COLUMNS.map(c => <col key={c.key} style={{ width: widthOf(c.key) }} />)}
                        </colgroup>
                        <thead>
                            <tr className="border-b">
                                {COLUMNS.map(c => (
                                    <SortableHeader
                                        key={c.key}
                                        label={c.label}
                                        sortKey={c.key}
                                        sort={sort}
                                        onSort={onSort}
                                        align={c.align}
                                        onResizeStart={x => startResize(c.key, x)}
                                        onResetWidth={() => resetWidth(c.key)}
                                        isResizing={resizingKey === c.key}
                                        filter={
                                            <ColumnFilter
                                                label={c.label}
                                                values={uniqueValuesFor(c.key)}
                                                state={filters[c.key]}
                                                onChange={s => setColumnFilter(c.key, s)}
                                                numeric={NUMERIC.includes(c.key)}
                                                active={isFiltered(c.key)}
                                                labels={FILTER_LABELS}
                                            />
                                        }
                                    />
                                ))}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {sorted.map(r => {
                                const planned = plannedDaysOf(r);
                                const approxTitle = r.approximate ? approximateTitle(r) : null;
                                const entriesTitle = r.plannedEntries
                                    .map(e => `${formatRange(e.from, e.to)} · ${e.label}${e.plannedDays === null ? '' : ` · ${formatDays(e.plannedDays)} d`}${e.isApproximate ? ' (≈)' : ''}`)
                                    .join('\n');
                                return (
                                    <tr key={r.employeeId ?? `hr-${r.staffId}`} className="hover:bg-gray-50">
                                        <td className="px-3 py-1.5 font-medium truncate" title={r.name}>
                                            {r.name}
                                            {!r.isActive && <span className="ml-1 text-xs text-gray-400">(left)</span>}
                                            <DiscrepancyBadges items={r.discrepancies} />
                                        </td>
                                        <td className="px-3 py-1.5 text-gray-600">{r.companyCode?.toUpperCase() ?? '—'}</td>
                                        {r.balanceMin === null ? (
                                            <td className="px-3 py-1.5 text-right text-gray-400" title="no vacation balance synced">—</td>
                                        ) : r.balanceDays === null ? (
                                            // No conversion rate: minutes are the only honest figure (trap 6).
                                            <td className="px-3 py-1.5 text-right tabular-nums text-gray-500" title="no conversion rate">{r.balanceMin} min</td>
                                        ) : (
                                            <td className={`px-3 py-1.5 text-right tabular-nums ${r.balanceDays < 0 ? 'text-red-600' : ''}`}
                                                title={r.balanceAsOf ? `as of ${formatDateLong(r.balanceAsOf)}` : undefined}>
                                                {formatDays(r.balanceDays)}
                                            </td>
                                        )}
                                        <td className={`px-3 py-1.5 text-right tabular-nums ${planned === 0 ? 'text-gray-400' : ''}`}
                                            title={approxTitle ?? (planned === null ? 'no conversion rate' : undefined)}>
                                            {planned === null
                                                ? <span className="text-gray-500">{r.plannedMin} min</span>
                                                : `${r.approximate ? '≈ ' : ''}${formatDays(planned)}`}
                                        </td>
                                        <td className="px-3 py-1.5 text-right tabular-nums">
                                            {r.restDays === null ? (
                                                <span className="text-gray-400" title="balance or planned figure missing">—</span>
                                            ) : (
                                                <span
                                                    className={`inline-block rounded-full px-2 py-0.5 ${FLAG_CLASS[r.flag]} ${r.flag === 'none' && r.restDays < 0 ? 'text-red-600' : ''}`}
                                                    title={[r.flag === 'red' ? 'red' : r.flag === 'yellow' ? 'yellow' : null, approxTitle].filter(Boolean).join(' · ') || undefined}
                                                >
                                                    {r.approximate ? '≈ ' : ''}{formatDays(r.restDays)}
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-3 py-1.5 text-gray-600 whitespace-nowrap tabular-nums">
                                            {r.nextVacation ? formatRange(r.nextVacation.from, r.nextVacation.to) : <span className="text-gray-400">—</span>}
                                        </td>
                                        <td className="px-3 py-1.5 text-right tabular-nums" title={entriesTitle || undefined}>
                                            {r.plannedEntries.length}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                        <tfoot className="bg-gray-50 border-t font-medium">
                            <tr>
                                <td className="px-3 py-2" colSpan={2}>
                                    {activeCount > 0 ? `${filtered.length} of ${rows.length} people` : `${summary.people} people`}
                                    {(sums.red > 0 || sums.yellow > 0) && (
                                        <span className="ml-2 font-normal text-gray-500">
                                            {sums.red > 0 && <span className="text-red-700">{sums.red} red</span>}
                                            {sums.red > 0 && sums.yellow > 0 && ' · '}
                                            {sums.yellow > 0 && <span className="text-amber-700">{sums.yellow} yellow</span>}
                                        </span>
                                    )}
                                </td>
                                <td className="px-3 py-2 text-right tabular-nums" title="Over the rows with a balance">{formatDays(sums.balance)}</td>
                                <td className="px-3 py-2 text-right tabular-nums" title="Over the rows with a conversion rate">{formatDays(sums.planned)}</td>
                                <td className={`px-3 py-2 text-right tabular-nums ${sums.rest < 0 ? 'text-red-600' : ''}`} title="Over the rows with both figures">
                                    {formatDays(sums.rest)}
                                </td>
                                <td />
                                <td className="px-3 py-2 text-right tabular-nums">{sums.entries}</td>
                            </tr>
                        </tfoot>
                    </table>
                )}
            </div>
        </div>
    );
}
